import _ from 'lodash';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import * as StarWebPrint from 'utils/printer/StarWebPrint.js';
import { NetworkPrinterJob, dashboard } from 'types';
import { PrinterPaperSize, rasterizeTextToImage } from 'utils/printer/networkPrinterUtils';
import { isLocalHostname } from 'settings/AppSettings';
import { toEnumString } from '@tock/coretypes';
import protos from 'utils/protos';

const { BusinessPrinter } = protos;

// This is a magic number which is the ratio between what a browser will indicate for a chit height and what the
// printer can handle.
const heightPadding = 0.95;

// For ensuring that only one job is sent to a web printer at a time.
const activePrintQueue: NetworkPrinterJob[] = [];

export function createWebPrinterPlainTextRequest(textToPrint: string): string {
  const builder = new StarWebPrint.StarWebPrintBuilder();
  return (
    builder.createTextElement({ data: textToPrint }) +
    builder.createFeedElement({ line: 1 }) +
    builder.createCutPaperElement({ feed: true, type: 'partial' })
  );
}

async function createWebPrinterRequest(textToPrint: string): Promise<string> {
  const result = await rasterizeTextToImage(
    textToPrint,
    PrinterPaperSize.THREE_INCHES,
    heightPadding
  );
  if (isLocalHostname(window.location.hostname)) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const blob = new Blob([result.renderResult.svg as any], {
      type: 'image/svg+xml',
    });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
  }
  const builder = new StarWebPrint.StarWebPrintBuilder();
  const canvas = result.canvas;
  const image = builder.createBitImageElement({
    context: canvas.getContext('2d'),
    x: 0,
    y: 0,
    width: canvas.width,
    height: canvas.height,
  });
  return (
    image +
    builder.createFeedElement({ line: 1 }) +
    builder.createCutPaperElement({ feed: true, type: 'partial' })
  );
}

function submitJobToWebPrint(job: NetworkPrinterJob): Promise<string> {
  return new Promise((resolve, reject) => {
    const trader = new StarWebPrint.StarWebPrintTrader({
      url: toWebPrintUri(job.printer),
      papertype: 'normal',
    });
    trader.onReceive = (response: { traderStatus: string }) => {
      const traderStatus = response.traderStatus;
      if (trader.isCoverOpen({ traderStatus })) {
        reject(`Could not print to ${job.printer.name} because the cover is open`);
        return;
      }
      if (trader.isPaperEnd({ traderStatus })) {
        reject(`Could not print to ${job.printer.name} because it is out of paper`);
        return;
      }
      resolve('');
    };
    trader.onError = () => {
      reject('A chit failed to print');
    };

    return createWebPrinterRequest(job.text).then((request) => trader.sendMessage({ request }));
  });
}

function webPrintPendingMessage(): Promise<string> {
  const firstJob = activePrintQueue[0];
  return submitJobToWebPrint(firstJob);
}

function webPrintFinished(onSuccess: () => void, onError: (err: string) => void): Promise<void> {
  activePrintQueue.shift();
  return _.isEmpty(activePrintQueue)
    ? Promise.resolve()
    : webPrintPendingMessage()
        .then(onSuccess)
        .catch(onError)
        .finally(() => webPrintFinished(onSuccess, onError));
}

export function submitJobsToWebPrint(
  jobs: NetworkPrinterJob[],
  onSuccess: () => void,
  onError: (err: string) => void
) {
  if (_.isEmpty(activePrintQueue)) {
    activePrintQueue.push(...jobs);
    webPrintPendingMessage()
      .then(onSuccess)
      .catch(onError)
      .finally(() => webPrintFinished(onSuccess, onError));
  } else {
    activePrintQueue.push(...jobs);
  }
}

interface NewOrExistingPrinter {
  ipAddress: string;
  communicationType: dashboard.BusinessPrinter.CommunicationType | string;
}

export function toBaseWebPrintUri(printer: NewOrExistingPrinter): string {
  const { communicationType, ipAddress } = printer;
  return (
    _.toLower(toEnumString(BusinessPrinter.CommunicationType, communicationType)) +
    '://' +
    ipAddress
  );
}

export function toWebPrintUri(printer: dashboard.BusinessPrinter): string {
  return toBaseWebPrintUri(printer) + '/StarWebPRNT/SendMessage';
}
